import React, { useEffect, useState, useRef } from "react";
import Dropdown from "../Util/Dropdown";
import * as ENV from "../Util/env";
import { selectYear } from "../Util/Analytics/InteractionAnalytics";

const YearDropdown = ({
  locationAbbr,
  onYearChange,
  questionId,
  subQuestionId,
  subQuestionText,
  summaryText,
  year,
  dropdownID = "Year",
  setDataLoading,
}) => {
  const [years, setYears] = useState([]);
  // Track if we're in the middle of a user-initiated year change to prevent
  // the validation effect from triggering cascading updates
  const isChangingYear = useRef(false);

  useEffect(() => {
    // Build the API URL with text matching parameters
    const params = new URLSearchParams();
    params.append("QuestionId", questionId);
    params.append("LocationAbbr", locationAbbr);
    
    // Only use text matching when EnableSubQuestionTextMatching is true AND
    // subQuestionText has a value (a subquestion is selected).
    // summaryText is only used as fallback when subQuestionText is empty but we still
    // need text matching (e.g., for questions without subquestions).
    // If neither has a value, no text matching is applied.
    if (ENV.EnableSubQuestionTextMatching && subQuestionText && subQuestionText.trim()) {
      params.append("MatchByText", "true");
      params.append("SubQuestionText", subQuestionText);
    } else if (ENV.EnableSubQuestionTextMatching && summaryText && summaryText.trim()) {
      // Only use summaryText fallback for questions that have no subquestions
      // The parent component should only pass summaryText when appropriate
      params.append("MatchByText", "true");
      params.append("SummaryText", summaryText);
    }

    fetch(ENV.APIBaseURL + "/Years?" + params.toString())
      .then((res) => res.json())
      .then((data) => {
        setYears(data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, locationAbbr, subQuestionText, summaryText]);

  // Validate that the currently selected year is available for this location.
  // If not, automatically select the most recent available year.
  // This should only run on initial load or location change, NOT during user-initiated year changes.
  useEffect(() => {
    // Skip validation if we're in the middle of a year change
    if (isChangingYear.current) return;
    if (!years || years.length === 0 || !year) return;
    
    // Check if the current year exists in the available years
    // Use string comparison to avoid type coercion issues
    const yearExists = years.some((y) => String(y.Year) === String(year));
    
    if (!yearExists) {
      // Year is not available for this location - select the most recent available year
      // Years are returned from API in descending order (most recent first)
      const mostRecentYear = years[0];
      if (mostRecentYear && onYearChange) {
        // Automatically trigger year change to the most recent available year
        handleYearChange(mostRecentYear, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years, locationAbbr]); // Removed 'year' from dependencies - only validate on initial load or location change

  // When the year selection changes, fetch questions (and their subquestions)
  // for that year and forward both the year and question list to the parent via onYearChange.
  async function handleYearChange(selection, isUserInitiated = true) {
    // selection is an object like { Year: 2020 }
    // Mark that we're changing years to prevent validation effect from interfering
    isChangingYear.current = true;

    if (isUserInitiated && dropdownID === "current-year-dropdown") {
      selectYear();
    }
    
    try {
      // Show loading screen while fetching questions for the new year
      if (setDataLoading) setDataLoading(true);
      
      // Fetch questions for the selected year with location context.
      // Include both filteredYear and LocationAbbr to get the correct SubQuestions.
      const params = new URLSearchParams();
      params.append("filteredYear", selection.Year);
      if (locationAbbr) params.append("LocationAbbr", locationAbbr);
      
      const res = await fetch(ENV.APIBaseURL + "/Questions?" + params.toString());
      const questions = await res.json();

      // Notify parent with both the year and questions payload so the app can update its state.
      // Only call onYearChange once to avoid intermediate state issues.
      if (onYearChange) onYearChange({ Year: selection.Year, Questions: questions });
    } catch (err) {
      // If fetching questions fails, still notify about the year change
      if (onYearChange) onYearChange(selection);
      console.error("Error fetching questions for year", selection.Year, err);
    } finally {
      // Hide loading screen after processing
      if (setDataLoading) setDataLoading(false);
      // Reset the flag after a longer delay to allow state updates and URL navigation to complete
      setTimeout(() => { isChangingYear.current = false; }, 600);
    }
  }

  const additionalOptions = [];

  return (
    <>
      {years && years.length > 0 && (
          <Dropdown
            options={years}
            itemID="Year"
            itemName="Year"
            dropdownTitle="Year"
            dropdownID={dropdownID}
            value={year}
            onChange={handleYearChange}
            additionalOptions={additionalOptions}
            emptyText="Select a year"
            showEmpty={false}
          />
      )}
    </>
  );
};

export default YearDropdown;
